// Kolefnisspor úrgangs — losunarstuðlar og útreikningar.
// Speglað úr Lausnir minar-sidur/lib/kolefnisspor.ts (Scope 3 úrgangslosun,
// GHG Protocol). Trimmað: einungis live-records + monthly-nálgun (ekki Reitir).

import { getMaterialInfo } from './materials'
import { UNKNOWN_GROUP, normalizeLocation } from './waste'
import type { Collection, IGFMonthlyData, MaterialCategory, DisposalMethod, IncinerationClass } from './waste'

export interface WasteEntry {
  month: number
  category: MaterialCategory
  group: string
  disposal: DisposalMethod
  kg: number
  materialId?: string
  materialName?: string
  location?: string
  postcode?: string
}

export const CATEGORY_TO_GROUP: Record<MaterialCategory, string> = {
  malmar: 'Málmar',
  timbur: 'Timbur',
  steinefni: 'Óvirkur úrgangur',
  plast: 'Plast',
  pappir: 'Pappír',
  gler: 'Óvirkur úrgangur',
  lifraen: 'Lífrænn úrgangur',
  oflokkadur: 'Óflokkað efni/blandað efni',
  byggingarurgangur: 'Óflokkað efni/blandað efni',
  spilliefni: 'Spilliefni',
  raftaeki: 'Raftæki',
  jardefni: 'Óvirkur úrgangur',
  orkuvinnsla: 'Óflokkað efni/blandað efni',
  annad: 'Annað',
}

/**
 * How the waste in a single category is *actually* split across disposal routes
 * for a given customer. Weights are relative (they need not sum to 1 — they are
 * normalised at use). A route with weight 0 or absent is ignored.
 *
 * Example (Landspítali óflokkað): { brennsla: 60, endurvinnsla: 30, urdun: 10 }
 */
export type DisposalSplit = Partial<Record<DisposalMethod, number>>

/**
 * Per-company override of {@link CATEGORY_DEFAULT_DISPOSAL}. Only the categories
 * present are overridden; the rest fall back to the default single-route mapping.
 * This is what lets a customer say "our unsorted waste mostly goes to energy
 * recovery, not landfill" so the footprint reflects reality.
 */
export type CarbonRouting = Partial<Record<MaterialCategory, DisposalSplit>>

/**
 * Emit one WasteEntry per configured disposal route, splitting <c>kg</c> by the
 * relative weights in <c>split</c>. Falls back to a single entry on the given
 * disposal when there is no usable split.
 */
function pushSplit(
  out: WasteEntry[],
  month: number,
  category: MaterialCategory,
  group: string,
  kg: number,
  fallbackDisposal: DisposalMethod,
  routing?: CarbonRouting | null,
  materialSplit?: DisposalSplit,
  materialId?: string,
  materialName?: string,
  location?: string,
  postcode?: string
): void {
  const split = routing?.[category] ?? materialSplit
  if (split) {
    let total = 0
    for (const d of ALL_DISPOSALS) {
      const w = split[d]
      if (typeof w === 'number' && w > 0) total += w
    }
    if (total > 0) {
      for (const d of ALL_DISPOSALS) {
        const w = split[d]
        if (typeof w === 'number' && w > 0) {
          out.push({ month, category, group, disposal: d, kg: kg * (w / total), materialId, materialName, location, postcode })
        }
      }
      return
    }
  }
  out.push({ month, category, group, disposal: fallbackDisposal, kg, materialId, materialName, location, postcode })
}

export type CarbonFactorKey =
  | 'recycling'
  | 'combustion'
  | 'special'
  | 'clinicalIncineration'
  | 'hazardousSolidIncineration'
  | 'hazardousLiquidIncineration'
  | 'organicIncineration'
  | 'mixedLandfill'
  | 'organicRecycle'
  | 'aggregateRecycle'
  | 'aggregateLandfill'

export interface CarbonFactorDef {
  byYear: Record<number, number>
  source?: string
}

/**
 * Superadmin-editable override of {@link DEFAULT_CARBON_FACTORS}, stored as the
 * `carbonFactors` app setting. A factor with a non-empty byYear replaces the
 * default table wholesale; absent factors fall back to the defaults.
 */
export type CarbonFactors = Partial<Record<CarbonFactorKey, CarbonFactorDef>>

export const CARBON_FACTOR_INFO: Record<CarbonFactorKey, { label: string; appliesTo: string }> = {
  recycling: {
    label: 'Endurvinnsla',
    appliesTo: 'Endurvinnsla flestra flokka',
  },
  combustion: {
    label: 'Brennsla með orkunýtingu',
    appliesTo: 'Úrgangur í orkunýtingu (R1)',
  },
  special: {
    label: 'Brennsla án orkunýtingar, blandað',
    appliesTo: 'Blandaður úrgangur, gifs, sýrur og basar og ólífræn spilliefni í brennslu (D10, Kalka)',
  },
  clinicalIncineration: {
    label: 'Brennsla sóttmengaðs úrgangs',
    appliesTo: 'Sóttmengaður úrgangur í brennslu (D10)',
  },
  hazardousSolidIncineration: {
    label: 'Brennsla fastra spilliefna',
    appliesTo: 'Spilliefni, kítti og sparsl, olíumengaður úrgangur og önnur efni til eyðingar (D10)',
  },
  hazardousLiquidIncineration: {
    label: 'Brennsla olíu og leysiefna',
    appliesTo: 'Úrgangsolía, smurfeiti, leysiefni, olíumálning, prentlitir og frostlögur í brennslu (D10)',
  },
  organicIncineration: {
    label: 'Brennsla lífræns úrgangs',
    appliesTo: 'Dýrahræ, fita, svínaskítur og sláturúrgangur í brennslu (D10)',
  },
  mixedLandfill: {
    label: 'Urðun blandaðs úrgangs',
    appliesTo: 'Urðun allra flokka nema stein- og jarðefna',
  },
  organicRecycle: {
    label: 'Jarðgerð lífræns úrgangs',
    appliesTo: 'Lífrænn úrgangur í endurvinnslu',
  },
  aggregateRecycle: {
    label: 'Endurvinnsla stein-/jarðefna',
    appliesTo: 'Steinefni og jarðefni í endurvinnslu',
  },
  aggregateLandfill: {
    label: 'Urðun stein-/jarðefna',
    appliesTo: 'Steinefni og jarðefni í urðun',
  },
}

const DEFRA = 'UK Government GHG Conversion Factors for Company Reporting (DESNZ), Waste disposal, árgangur fylgir gagnaári'

const DEFRA_GENERIC: Record<number, number> = {
  2020: 0.0213167, 2021: 0.0212936, 2022: 0.0212801,
  2023: 0.0212808, 2024: 0.00641061, 2025: 0.00468568, 2026: 0.00465358,
}

export const DEFRA_FACTORS: Record<CarbonFactorKey, CarbonFactorDef> = {
  recycling: { byYear: DEFRA_GENERIC, source: DEFRA },
  combustion: { byYear: DEFRA_GENERIC, source: DEFRA },
  special: { byYear: DEFRA_GENERIC, source: DEFRA },
  clinicalIncineration: { byYear: DEFRA_GENERIC, source: DEFRA },
  hazardousSolidIncineration: { byYear: DEFRA_GENERIC, source: DEFRA },
  hazardousLiquidIncineration: { byYear: DEFRA_GENERIC, source: DEFRA },
  organicIncineration: { byYear: DEFRA_GENERIC, source: DEFRA },
  mixedLandfill: {
    byYear: {
      2020: 0.4373719, 2021: 0.4462415, 2022: 0.4462041,
      2023: 0.4970447, 2024: 0.49704416, 2025: 0.49724244, 2026: 0.49728993,
    },
    source: DEFRA,
  },
  organicRecycle: {
    byYear: {
      2020: 0.0102039, 2021: 0.0089507, 2022: 0.0089106,
      2023: 0.0089124, 2024: 0.00888386, 2025: 0.00898311, 2026: 0.00900687,
    },
    source: DEFRA,
  },
  aggregateRecycle: {
    byYear: {
      2020: 0.0012489, 2021: 0.0009891, 2022: 0.0009847,
      2023: 0.0009849, 2024: 0.00098485, 2025: 0.00100835, 2026: 0.00101398,
    },
    source: DEFRA,
  },
  aggregateLandfill: {
    byYear: {
      2020: 0.0012489, 2021: 0.0012393, 2022: 0.0012338,
      2023: 0.0012340, 2024: 0.00123393, 2025: 0.00126338, 2026: 0.00127043,
    },
    source: DEFRA,
  },
}

export function parseCarbonFactors(raw: unknown): CarbonFactors | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null
  const out: CarbonFactors = {}
  for (const key of Object.keys(CARBON_FACTOR_INFO) as CarbonFactorKey[]) {
    const entry = (raw as Record<string, unknown>)[key]
    if (entry == null || typeof entry !== 'object') continue
    const byYearRaw = (entry as Record<string, unknown>).byYear
    if (byYearRaw == null || typeof byYearRaw !== 'object') continue
    const byYear: Record<number, number> = {}
    for (const [y, v] of Object.entries(byYearRaw as Record<string, unknown>)) {
      const year = Number(y)
      if (typeof v !== 'number') continue
      if (Number.isInteger(year) && Number.isFinite(v) && v >= 0) byYear[year] = v
    }
    if (Object.keys(byYear).length === 0) continue
    const source = (entry as Record<string, unknown>).source
    out[key] = { byYear, source: typeof source === 'string' && source.trim() ? source : undefined }
  }
  return Object.keys(out).length > 0 ? out : null
}

function factorTable(key: CarbonFactorKey, factors?: CarbonFactors | null): Record<number, number> {
  const byYear = factors?.[key]?.byYear
  return byYear && Object.keys(byYear).length > 0 ? byYear : DEFAULT_CARBON_FACTORS[key].byYear
}

function valueForYear(table: Record<number, number>, year: number): number {
  if (table[year] != null) return table[year]
  const years = Object.keys(table).map(Number).sort((a, b) => a - b)
  let chosen = years[0]
  for (const y of years) {
    if (y <= year) chosen = y
    else break
  }
  return table[chosen]
}

export function recyclingFactor(year: number, factors?: CarbonFactors | null): number {
  return valueForYear(factorTable('recycling', factors), year)
}

export function combustionFactor(year: number, factors?: CarbonFactors | null): number {
  return valueForYear(factorTable('combustion', factors), year)
}

const UOS = 'UOS — Losunarstuðlar Umhverfis- og orkustofnunar, 7. útgáfa (des. 2025)'
const IPCC_INCINERATION = 'IPCC 2006 Guidelines, 5. bindi, kafli 5, tafla 5.2 (sömu aðferð og UOS notar fyrir blandað sorp), staðgengill þar til Kalka gefur upp eigin tölu'

const INCINERATION_KEY: Record<IncinerationClass, CarbonFactorKey> = {
  mixed: 'special',
  clinical: 'clinicalIncineration',
  hazardousSolid: 'hazardousSolidIncineration',
  hazardousLiquid: 'hazardousLiquidIncineration',
  organic: 'organicIncineration',
}

function incinerationKey(category: MaterialCategory, materialId?: string): CarbonFactorKey {
  const cls = materialId ? getMaterialInfo(materialId).incineration : undefined
  if (cls) return INCINERATION_KEY[cls]
  if (category === 'spilliefni') return 'hazardousSolidIncineration'
  if (category === 'lifraen') return 'organicIncineration'
  return 'special'
}

/**
 * The agreed factor policy: Icelandic UOS factors wherever they exist, DEFRA
 * filling the gaps. Loaded into the admin editor by the "Setja UOS-blönduna"
 * button — never applied implicitly. UOS values are keyed at 2020 so they cover
 * every data year uniformly (UOS publishes a single current value, not a series).
 */
export const UOS_DEFRA_BLEND: Record<CarbonFactorKey, CarbonFactorDef> = {
  recycling: { byYear: { ...DEFRA_GENERIC }, source: DEFRA + ' — endurvinnsla erlendis; staðgengill í stað UOS 0 að ákvörðun ÍGF (Elín, 15.9.2026); nær yfir söfnun, flutning og fyrstu flokkun, ekki endurvinnsluferlið sjálft' },
  combustion: { byYear: { ...DEFRA_GENERIC }, source: DEFRA + ' — orkunýting erlendis; staðgengill, UOS hefur engan stuðul; nær yfir söfnun og flutning, brennslan sjálf bókfærist hjá orkunotanda' },
  special: { byYear: { 2020: 0.526 }, source: UOS + ' — blandaður heimilisúrgangur til brennslu innanlands án orkunýtingar' },
  clinicalIncineration: { byYear: { 2020: 0.88 }, source: IPCC_INCINERATION + ': sóttmengaður úrgangur, kolefni 60 %, þar af 40 % úr jarðefnum' },
  hazardousSolidIncineration: { byYear: { 2020: 1.65 }, source: IPCC_INCINERATION + ': spilliefni, kolefni 50 %, þar af 90 % úr jarðefnum' },
  hazardousLiquidIncineration: { byYear: { 2020: 2.93 }, source: IPCC_INCINERATION + ': fljótandi jarðefnaúrgangur, kolefni 80 %, allt úr jarðefnum; UOS gefur 3,02 fyrir úrgangsolíu' },
  organicIncineration: { byYear: { 2020: 0.013 }, source: IPCC_INCINERATION + ': lífrænt CO₂ undanskilið, aðeins N₂O (50 g/t) og CH₄' },
  mixedLandfill: { byYear: { 2020: 1.1 }, source: UOS },
  organicRecycle: { byYear: { 2020: 0.207 }, source: UOS + ' — GAJA gas- og jarðgerðarstöð (minnisblað ÍGF); Molta Eyjafirði án stuðuls' },
  aggregateRecycle: { byYear: { ...DEFRA_FACTORS.aggregateRecycle.byYear }, source: DEFRA + ' — UOS hefur engan stuðul' },
  aggregateLandfill: { byYear: { ...DEFRA_FACTORS.aggregateLandfill.byYear }, source: DEFRA + ' — UOS hefur engan stuðul' },
}

export const DEFAULT_CARBON_FACTORS: Record<CarbonFactorKey, CarbonFactorDef> = UOS_DEFRA_BLEND

const DESNZ_SERIES_KEYS: CarbonFactorKey[] = ['recycling', 'combustion', 'aggregateRecycle', 'aggregateLandfill']

export function withDesnz2023Series(factors?: CarbonFactors | null): CarbonFactors {
  const out: CarbonFactors = { ...(factors ?? {}) }
  for (const key of DESNZ_SERIES_KEYS) {
    out[key] = { byYear: { 2023: valueForYear(factorTable(key, factors), 2023) }, source: DEFAULT_CARBON_FACTORS[key].source }
  }
  return out
}

export function landfillFactor(year: number, factors?: CarbonFactors | null): number {
  return valueForYear(factorTable('mixedLandfill', factors), year)
}

export function getEmissionFactor(
  category: MaterialCategory,
  disposal: DisposalMethod,
  year: number,
  factors?: CarbonFactors | null,
  materialId?: string
): number {
  const isAggregate = category === 'steinefni' || category === 'jardefni' || category === 'gler'

  if (disposal === 'urdun') {
    return isAggregate
      ? valueForYear(factorTable('aggregateLandfill', factors), year)
      : valueForYear(factorTable('mixedLandfill', factors), year)
  }

  if (disposal === 'endurvinnsla') {
    if (category === 'lifraen') return valueForYear(factorTable('organicRecycle', factors), year)
    if (isAggregate) return valueForYear(factorTable('aggregateRecycle', factors), year)
    return recyclingFactor(year, factors)
  }

  if (disposal === 'brennsla') return valueForYear(factorTable(incinerationKey(category, materialId), factors), year)

  return combustionFactor(year, factors)
}

export const CATEGORY_DEFAULT_DISPOSAL: Record<MaterialCategory, DisposalMethod> = {
  malmar: 'endurvinnsla',
  timbur: 'endurvinnsla',
  steinefni: 'endurvinnsla',
  plast: 'endurvinnsla',
  pappir: 'endurvinnsla',
  gler: 'urdun',
  lifraen: 'endurvinnsla',
  oflokkadur: 'orkunyting',
  byggingarurgangur: 'urdun',
  spilliefni: 'brennsla',
  raftaeki: 'endurvinnsla',
  jardefni: 'urdun',
  orkuvinnsla: 'orkunyting',
  annad: 'endurvinnsla',
}

function monthOf(dateValue: string | Date): number {
  if (typeof dateValue === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateValue.trim())
    if (m) return Number(m[2])
  }
  const d = dateValue instanceof Date ? dateValue : new Date(dateValue)
  const m = d.getMonth()
  return Number.isNaN(m) ? 0 : m + 1
}

export function entriesFromRecords(records: Collection[], routing?: CarbonRouting | null): WasteEntry[] {
  const out: WasteEntry[] = []
  for (const r of records) {
    if (!r.weight || r.weight <= 0) continue
    const info = getMaterialInfo(r.materialId || '')
    const month = r.dateCollected ? monthOf(r.dateCollected) : 0
    // The material's own disposal is the fallback; a per-company routing override
    // for this category (e.g. óflokkað mostly to energy recovery) takes precedence.
    const group = r.materialGroup?.trim() || CATEGORY_TO_GROUP[info.category] || UNKNOWN_GROUP
    pushSplit(out, month, info.category, group, r.weight, r.disposal ?? info.disposal, routing, r.disposal ? undefined : info.split, r.materialId || '', info.name, normalizeLocation(r.location), r.postcode?.trim() || undefined)
  }
  return out
}

export function entriesFromMonthly(monthly: IGFMonthlyData[], routing?: CarbonRouting | null): WasteEntry[] {
  const out: WasteEntry[] = []
  for (const m of monthly) {
    if (!m.categories) continue
    for (const [cat, kg] of Object.entries(m.categories) as [MaterialCategory, number][]) {
      if (!kg || kg <= 0) continue
      pushSplit(out, m.month, cat, CATEGORY_TO_GROUP[cat] || UNKNOWN_GROUP, kg, CATEGORY_DEFAULT_DISPOSAL[cat], routing)
    }
  }
  return out
}

export interface Co2eAggregate {
  totalCo2e: number
  totalKg: number
  co2ePerTonne: number
  byGroup: { group: string; kg: number; co2e: number }[]
  byMaterial: { group: string; materialId: string; materialName: string; disposal: DisposalMethod; kg: number; co2e: number }[]
  byDisposal: { disposal: DisposalMethod; kg: number; co2e: number }[]
  byLocation: { location: string; postcode?: string; kg: number; co2e: number; materials: { group: string; materialId: string; materialName: string; disposal: DisposalMethod; kg: number; co2e: number }[] }[]
  byMonth: number[]
}

const ALL_DISPOSALS: DisposalMethod[] = ['endurvinnsla', 'orkunyting', 'brennsla', 'urdun']

export function aggregateCo2e(
  entries: WasteEntry[],
  year: number,
  factors?: CarbonFactors | null
): Co2eAggregate {
  const groupMap = new Map<string, { kg: number; co2e: number }>()
  const materialMap = new Map<string, { group: string; materialId: string; materialName: string; disposal: DisposalMethod; kg: number; co2e: number }>()
  const dispMap = new Map<DisposalMethod, { kg: number; co2e: number }>()
  const locationMap = new Map<string, { postcode?: string; kg: number; co2e: number; materials: Map<string, { group: string; materialId: string; materialName: string; disposal: DisposalMethod; kg: number; co2e: number }> }>()
  const byMonth = new Array<number>(12).fill(0)

  let totalCo2e = 0
  let totalKg = 0

  for (const e of entries) {
    const factor = getEmissionFactor(e.category, e.disposal, year, factors, e.materialId)
    const co2e = e.kg * factor

    totalCo2e += co2e
    totalKg += e.kg

    const c = groupMap.get(e.group) ?? { kg: 0, co2e: 0 }
    c.kg += e.kg
    c.co2e += co2e
    groupMap.set(e.group, c)

    if (e.materialId) {
      const mk = e.materialId + '|' + e.disposal
      const mm = materialMap.get(mk) ?? { group: e.group, materialId: e.materialId, materialName: e.materialName ?? e.materialId, disposal: e.disposal, kg: 0, co2e: 0 }
      mm.kg += e.kg
      mm.co2e += co2e
      materialMap.set(mk, mm)
    }

    const d = dispMap.get(e.disposal) ?? { kg: 0, co2e: 0 }
    d.kg += e.kg
    d.co2e += co2e
    dispMap.set(e.disposal, d)

    if (e.location) {
      const l = locationMap.get(e.location) ?? { postcode: undefined, kg: 0, co2e: 0, materials: new Map() }
      l.kg += e.kg
      l.co2e += co2e
      if (!l.postcode && e.postcode) l.postcode = e.postcode
      if (e.materialId) {
        const lmk = e.materialId + '|' + e.disposal
        const lm = l.materials.get(lmk) ?? { group: e.group, materialId: e.materialId, materialName: e.materialName ?? e.materialId, disposal: e.disposal, kg: 0, co2e: 0 }
        lm.kg += e.kg
        lm.co2e += co2e
        l.materials.set(lmk, lm)
      }
      locationMap.set(e.location, l)
    }

    if (e.month >= 1 && e.month <= 12) byMonth[e.month - 1] += co2e
  }

  const byGroup = Array.from(groupMap.entries())
    .map(([group, v]) => ({ group, kg: v.kg, co2e: v.co2e }))
    .sort((a, b) => b.co2e - a.co2e)

  const byMaterial = Array.from(materialMap.values()).sort((a, b) => b.co2e - a.co2e)

  const byLocation = Array.from(locationMap.entries())
    .map(([location, v]) => ({
      location,
      postcode: v.postcode,
      kg: v.kg,
      co2e: v.co2e,
      materials: Array.from(v.materials.values()).sort((a, b) => b.co2e - a.co2e),
    }))
    .sort((a, b) => b.co2e - a.co2e)

  const byDisposal = ALL_DISPOSALS
    .map((disposal) => {
      const v = dispMap.get(disposal) ?? { kg: 0, co2e: 0 }
      return { disposal, kg: v.kg, co2e: v.co2e }
    })
    .filter((d) => d.kg > 0)

  return {
    totalCo2e,
    totalKg,
    co2ePerTonne: totalKg > 0 ? (totalCo2e / totalKg) * 1000 : 0,
    byGroup,
    byMaterial,
    byDisposal,
    byLocation,
    byMonth,
  }
}

export interface CarbonFactorSource {
  key: CarbonFactorKey
  label: string
  appliesTo: string
  value: number
  source: string
  isOverride: boolean
}

export function carbonFactorSources(year: number, factors?: CarbonFactors | null): CarbonFactorSource[] {
  return (Object.keys(CARBON_FACTOR_INFO) as CarbonFactorKey[]).map((key) => {
    const override = factors?.[key]
    const isOverride = !!(override?.byYear && Object.keys(override.byYear).length > 0)
    const def = isOverride ? override! : DEFAULT_CARBON_FACTORS[key]
    return {
      key,
      label: CARBON_FACTOR_INFO[key].label,
      appliesTo: CARBON_FACTOR_INFO[key].appliesTo,
      value: valueForYear(def.byYear, year),
      source: def.source?.trim() || DEFAULT_CARBON_FACTORS[key].source || '',
      isOverride,
    }
  })
}
